<script setup>
import { ref, computed, onMounted } from "vue";
import { helpers, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import Autocomplete from "@/components/job-position/forms/elements/Autocomplete.vue";
import jobPositionStatuses from "@/config/lists/jobPositionStatuses";
import applyWays from "@/config/lists/applyWays";
import foundWays from "@/config/lists/foundWays";
import { useJobPositionStore, useListStore, useUserStore } from "@/stores";
import { useRoute, useRouter } from "vue-router";

import contractTypes from "@/config/lists/contractTypes";
import workingHours from "@/config/lists/workingHours";
import workingShifts from "@/config/lists/workingShifts";
import workWeekSchedules from "@/config/lists/workWeekSchedules";
import salaryRange from "@/config/lists/salaryRange";
import previousExperience from "@/config/lists/previousExperience";
import gendersJobPosition from "@/config/lists/gendersJobPosition";
import agePreferences from "@/config/lists/agePreferences";
import drivingLicenses from "@/config/lists/drivingLicenses";

const route = useRoute();
const router = useRouter();

import {
  BaseKit,
  Bold,
  Color,
  Fullscreen,
  Heading,
  Highlight,
  History,
  Image,
  Italic,
  Link,
  Strike,
  Table,
  Underline,
  Video,
  VuetifyTiptap,
} from "vuetify-pro-tiptap";
import "vuetify-pro-tiptap/style.css";
import { consoleError } from "vuetify/lib/util/console.mjs";

const extensions = [
  BaseKit.configure({
    placeholder: {
      placeholder: "Enter some text...",
    },
  }),
  Bold,
  Italic,
  Underline,
  Strike,
  Color,
  Highlight,
  Heading,
  Link,
  Image,
  Video,
  Table,
  Fullscreen,
  History,
];

const userStore = useUserStore();
const listStore = useListStore();
const jobPositionStore = useJobPositionStore();

const user = userStore.user;

const companies = computed(() => listStore.companies);
const communicationResponsibles = computed(
  () => listStore.communicationResponsibles
);

const searchSelected = ref(null);
const searchMatched = ref(null);
const searchHired = ref(null);
const selectedBeneficiaries = ref([]);
const matchedBeneficiaries = ref([]);
const hiredBeneficiaries = ref([]);
const workingHoursValue = ref(0);
const adminLocations = computed(() => listStore.adminLocations);
const adminLocationCities = computed(() => listStore.adminLocationCities);
const languages = computed(() => listStore.languages);
const countries = computed(() => listStore.countries);

const isLoading = ref(false);

const tickLabels = {
  0: "4 hours",
  1: "6 hours",
  2: "8 hours",
};

const onInput = async (val, type) => {
  if (val != null && val.length > 2) {
    isLoading.value = true;
    if (type === "selected") {
      selectedBeneficiaries.value = await listStore.fetchBeneficiaries({
        keyword: val,
      });
    } else if (type === "matched") {
      matchedBeneficiaries.value = await listStore.fetchBeneficiaries({
        keyword: val,
      });
    } else {
      hiredBeneficiaries.value = await listStore.fetchBeneficiaries({
        keyword: val,
      });
    }

    isLoading.value = false;
  } else {
    selectedBeneficiaries.value = [];
    matchedBeneficiaries.value = [];
    hiredBeneficiaries.value = [];
  }
};

const submitForm = async () => {
  const isValid = await v$.value.$validate();
  form.value.createdUserId = user.id;

  const data = form.value;

  if (isValid) {
    jobPositionStore.editJobPosition({ data, id: data.id });
  }
};

const rules = {
  name: {
    required: helpers.withMessage("Job Title is required", required),
  },
  positionId: {
    required: helpers.withMessage("Job Position (ID) is required", required),
  },
  company: {
    required: helpers.withMessage("Company is required", required),
  },
  status: {
    required: helpers.withMessage("Status is required", required),
  },
  totalPositions: {
    required: helpers.withMessage("Number of Positions is required", required),
  },
};

const form = ref({
  id: null,
  positionId: null,
  name: null,
  company: null,
  description: null,
  status: null,
  totalPositions: null,
  link: null,
  salary: null,
  foundWay: null,
  applyWay: null,
  comment: null,
  selectedBeneficiaries: null,
  matchedBeneficiaries: null,
  hiredBeneficiaries: null,
  location: null,
  parentLocation: null,
  contractType: null,
  workingHours: null,
  workingShifts: null,
  workWeekSchedule: null,
  salaryRange: null,
  responsibilities: null,
  previousExperience: null,
  offersTraining: null,
  requiredLanguages: null,
  nationalities: null,
  gender: null,
  age: null,
  requiresDrivingLicense: null,
  drivingLicense: null,
  openToPwD: null,
  softSkills: null,
  contactWhatsapp: null,
  additionalInfo: null,
  offersTransportation: null,
});

const v$ = useVuelidate(rules, form);

const isRequired = (field) => {
  return "required" in rules[field];
};

const backToOverview = () => {
  router.push("/company/job-position/overview");
};

const getErrorMessages = (field) =>
  computed(() =>
    v$.value[field]?.$dirty && v$.value[field].$error
      ? v$.value[field].$errors.map((e) => e.$message)
      : []
  );

async function onAdminLocationSelected(value) {
  form.value.location = null;
  await listStore.fetchAdminLocationCities(value.id);
}

onMounted(async () => {
  const data = await jobPositionStore.getJobPosition({
    id: route.params.id,
  });

  let index = -1;
  for (const [i, wh] of workingHours.entries()) {
    if (wh.value === data.workingHours?.value) {
      index = i;
      break;
    }
  }
  workingHoursValue.value = index;

  form.value = data;

  await listStore.fetchCompanies();
  await listStore.fetchAdminLocations();
  if (form.value?.parentLocation) {
    await listStore.fetchAdminLocationCities(form.value?.parentLocation.id);
  }
  await listStore.fetchLanguages();
  await listStore.fetchCountries();
  await listStore.fetchCommunicationResponsibles();
});
</script>

<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="6">
        <h1 class="text-company">Edit Job Position</h1>
      </v-col>
      <v-col cols="6" class="d-flex justify-end align-center">
        <v-btn
          size="small"
          color="black"
          variant="text"
          class="mr-5"
          @click="backToOverview"
        >
          <v-icon>mdi-chevron-left</v-icon> back to overview
        </v-btn>
        <v-btn color="company" size="large" @click="submitForm">
          <v-icon icon="mdi-content-save" /> Save changes
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-form class="custom-form mt-5 d-flex flex-column">
          <v-text-field
            v-model="form.positionId"
            name="positionId"
            :error-messages="getErrorMessages('positionId').value"
            hide-details="auto"
            @blur="v$.positionId.$touch()"
          >
            <template #label>
              Job Position (ID)
              <span v-if="isRequired('positionId')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-text-field>

          <Autocomplete
            v-model="form.company"
            label="Company"
            :items="companies"
            :multiple="false"
            :chips="false"
            :blur-check="() => v$.company.$touch()"
            :error-messages="getErrorMessages('company').value"
            :is-required="isRequired('company')"
          />

          <v-text-field
            v-model="form.name"
            name="name"
            hide-details="auto"
            :error-messages="getErrorMessages('name').value"
            @blur="v$.name.$touch()"
          >
            <template #label>
              Job Title
              <span v-if="isRequired('positionId')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-text-field>

          <v-select
            v-model="form.parentLocation"
            label="Area"
            :items="adminLocations"
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
            @update:model-value="onAdminLocationSelected"
          />

          <v-select
            v-if="form.parentLocation"
            v-model="form.location"
            label="Municipality/Suburb"
            :items="adminLocationCities"
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
          />

          <VuetifyTiptap
            v-model="form.description"
            label="Description"
            rounded
            :min-height="200"
            :extensions="extensions"
          />

          <v-select
            v-model="form.contractType"
            :items="contractTypes"
            clearable
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
          >
            <template #label> What is the type of contract? </template>
          </v-select>

          <v-slider
            v-model="workingHoursValue"
            :min="0"
            :max="2"
            :ticks="tickLabels"
            show-ticks="always"
            color="primary"
            step="1"
            tick-size="5"
            @change="workingHoursChanged"
          >
            <template #label> Working Hours </template>
          </v-slider>

          <Autocomplete
            v-model="form.workingShifts"
            label="Working Shifts"
            :items="workingShifts"
            :multiple="true"
            :chips="true"
          />

          <v-select
            v-model="form.workWeekSchedule"
            :items="workWeekSchedules"
            clearable
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
          >
            <template #label> Workweek schedule </template>
          </v-select>

          <v-select
            v-if="false"
            v-model="form.salaryRange"
            :items="salaryRange"
            clearable
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
          >
            <template #label> Salary range </template>
          </v-select>

          <VuetifyTiptap
            v-model="form.responsibilities"
            label="Responsibilities for the position"
            rounded
            :min-height="200"
            :extensions="extensions"
          />

          <v-select
            v-model="form.previousExperience"
            :items="previousExperience"
            clearable
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
          >
            <template #label> Previous Experience </template>
          </v-select>

          <RadioGroup
            v-model="form.offersTraining"
            label="Does the position offer any training?"
            :error-messages="getErrorMessages('offersTraining').value"
            blur-check="v$.hasAfm.$touch()"
          />

          <v-combobox
            v-model="form.requiredLanguages"
            chips
            clear-on-select
            multiple
            closable-chips
            return-object
            item-title="name"
            item-value="id"
            variant="outlined"
            label="Required Languages"
            hint="Hint: You can add a new language by typing the name and pressing the 'Enter' key"
            persistent-hint
            hide-details="auto"
            :items="languages"
            @update:model-value="onSelect"
          />

          <v-combobox
            v-model="form.nationalities"
            chips
            clear-on-select
            multiple
            closable-chips
            return-object
            item-title="name"
            item-value="id"
            variant="outlined"
            label="Nationalities"
            hint="Hint: You can add a new nationality by typing the name and pressing the 'Enter' key"
            persistent-hint
            hide-details="auto"
            :items="countries"
            @update:model-value="onSelect"
          />

          <v-select
            v-model="form.gender"
            :items="gendersJobPosition"
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
          >
            <template #label> Gender preference </template>
          </v-select>

          <v-select
            v-model="form.age"
            :items="agePreferences"
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
          >
            <template #label> Age preference </template>
          </v-select>

          <RadioGroup
            v-model="form.requiresDrivingLicense"
            label="Does the position
          require a driver's licence?"
            :with-na="false"
          />

          <v-select
            v-if="form.requiresDrivingLicense == true"
            v-model="form.drivingLicense"
            :items="drivingLicenses"
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
          >
            <template #label>
              What type of driver's license does it require?
            </template>
          </v-select>

          <RadioGroup
            v-model="form.openToPwD"
            label="Open to People with Disabilities?"
            :with-na="false"
          />

          <VuetifyTiptap
            v-model="form.softSkills"
            label="Required soft skills for the position"
            rounded
            :min-height="200"
            :extensions="extensions"
          />

          <RadioGroup
            v-model="form.contactWhatsapp"
            label="Contact through Whatsapp?"
            :with-na="false"
          />

          <VuetifyTiptap
            v-model="form.additionalInfo"
            label="Additional information for the position"
            rounded
            :min-height="200"
            :extensions="extensions"
          />

          <RadioGroup
            v-model="form.offersTransportation"
            label="Offers transportation from/to work?"
            :with-na="false"
          />

          <v-select
            v-model="form.status"
            :items="jobPositionStatuses"
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
            :error-messages="getErrorMessages('status').value"
            @blur="v$.status.$touch()"
          >
            <template #label>
              Status
              <span v-if="isRequired('status')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-select>

          <v-number-input
            v-model="form.totalPositions"
            :reverse="false"
            control-variant="stacked"
            label="Number of positions to cover"
            :hide-input="false"
            :inset="false"
            variant="outlined"
            :error-messages="getErrorMessages('totalPositions').value"
            @blur="v$.totalPositions.$touch()"
          >
            <template #label>
              Number of positions to cover
              <span v-if="isRequired('totalPositions')" class="red--text">
                <strong>* </strong>
              </span>
            </template>
          </v-number-input>

          <v-text-field v-model="form.link" name="link" hide-details="auto">
            <template #label> Link </template>
          </v-text-field>

          <v-number-input
            v-model="form.salary"
            :reverse="false"
            control-variant="stacked"
            label="Salary"
            :hide-input="false"
            :inset="false"
            variant="outlined"
          />

          <v-select
            v-model="form.foundWay"
            :items="foundWays"
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
          >
            <template #label> How did we find the job position? </template>
          </v-select>

          <v-select
            v-model="form.applyWay"
            :items="applyWays"
            item-title="name"
            item-value="value"
            return-object
            variant="outlined"
            hide-details="auto"
          >
            <template #label> How did we apply for the job post? </template>
          </v-select>

          <VuetifyTiptap
            v-model="form.comment"
            label="Comments"
            rounded
            :min-height="200"
            :extensions="extensions"
          />

          <v-autocomplete
            v-model="form.selectedBeneficiaries"
            v-model:search-input="searchSelected"
            multiple
            :loading="isLoading"
            chips
            closable-chips
            return-object
            clearable
            placeholder="Start typing to Search"
            item-title="name"
            item-value="id"
            :items="selectedBeneficiaries"
            variant="outlined"
            hide-details="auto"
            :menu-props="{ maxWidth: 0 }"
            @update:search="(val) => onInput(val, 'selected')"
          >
            <template #label> Selected Beneficiaries </template>
          </v-autocomplete>

          <v-autocomplete
            v-model="form.matchedBeneficiaries"
            v-model:search-input="searchMatched"
            multiple
            :loading="isLoading"
            chips
            closable-chips
            return-object
            clearable
            placeholder="Start typing to Search"
            item-title="name"
            item-value="id"
            :items="matchedBeneficiaries"
            variant="outlined"
            hide-details="auto"
            :menu-props="{ maxWidth: 0 }"
            @update:search="(val) => onInput(val, 'matched')"
          >
            <template #label> Matched Beneficiaries </template>
          </v-autocomplete>

          <v-autocomplete
            v-model="form.hiredBeneficiaries"
            v-model:search-input="searchHired"
            multiple
            :loading="isLoading"
            chips
            closable-chips
            return-object
            clearable
            placeholder="Start typing to Search"
            item-title="name"
            item-value="id"
            :items="hiredBeneficiaries"
            variant="outlined"
            hide-details="auto"
            :menu-props="{ maxWidth: 0 }"
            @update:search="(val) => onInput(val, 'hired')"
          >
            <template #label> Hired Beneficiaries </template>
          </v-autocomplete>

          <Autocomplete
            v-model="form.responsible"
            label="Communication responsible"
            :items="communicationResponsibles"
            :multiple="false"
            :chips="false"
          />
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
h1 {
  color: rgb(var(--v-theme-company));
}
.flex-50 {
  flex: 0 0 50%;
}
</style>
